import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDocs,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../firebase';

const BANNERS_COLLECTION = 'app_offer_banners';
const ANALYTICS_COLLECTION = 'app_offer_banner_analytics';

export type AppOfferBannerEventType = 'impression' | 'cta_click' | 'dismiss';

/** Stored on `cta_click` rows so admins can split link vs button vs upgrade modal. */
export type AppOfferBannerCtaClickKind = 'link' | 'button' | 'upgrade_modal';

/** Outlined pill CTA vs subtle text link */
export type AppOfferBannerCtaStyle = 'button' | 'link';

/** Navigate to URL vs open in-app plan upgrade modal */
export type AppOfferBannerCtaTarget = 'url' | 'upgrade_modal';

/** Horizontal layout of badge + message + actions */
export type AppOfferBannerContentAlign = 'left' | 'center' | 'right';

/** Filled vs bordered CTA when `ctaStyle` is `button` */
export type AppOfferBannerCtaButtonVariant = 'outline' | 'solid' | 'ghost';

/** Corner radius for button-style CTA */
export type AppOfferBannerCtaButtonRadius = 'none' | 'md' | 'lg' | 'pill';

export function normalizeContentAlign(v: unknown): AppOfferBannerContentAlign {
  if (v === 'center' || v === 'right') return v;
  return 'left';
}

export function normalizeShowBadge(v: unknown): boolean {
  return v !== false;
}

export function normalizeShowCta(v: unknown): boolean {
  return v !== false;
}

export function normalizeCtaButtonVariant(v: unknown): AppOfferBannerCtaButtonVariant {
  if (v === 'solid' || v === 'ghost') return v;
  return 'outline';
}

export function normalizeCtaButtonRadius(v: unknown): AppOfferBannerCtaButtonRadius {
  if (v === 'none' || v === 'md' || v === 'lg' || v === 'pill') return v;
  return 'md';
}

const RADIUS_CLASS: Record<AppOfferBannerCtaButtonRadius, string> = {
  none: 'rounded-none',
  md: 'rounded-md',
  lg: 'rounded-lg',
  pill: 'rounded-full',
};

export function ctaButtonRadiusClass(r: AppOfferBannerCtaButtonRadius): string {
  return RADIUS_CLASS[r] ?? RADIUS_CLASS.md;
}

/** Outer row under the banner background (max-width wrapper applied in UI). */
export function bannerOuterLayoutClass(align: AppOfferBannerContentAlign): string {
  switch (align) {
    case 'center':
      return 'flex w-full flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-x-4 sm:gap-y-2';
    case 'right':
      return 'flex w-full flex-col gap-2 sm:flex-row-reverse sm:items-center sm:justify-between sm:gap-4';
    default:
      return 'flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4';
  }
}

export function bannerContentClusterClass(align: AppOfferBannerContentAlign): string {
  switch (align) {
    case 'center':
      return 'flex min-w-0 w-full flex-col items-center gap-2 sm:w-auto sm:max-w-3xl sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-3';
    case 'right':
      return 'flex min-w-0 flex-1 flex-col items-center gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3';
    default:
      return 'flex min-w-0 flex-1 flex-col items-center gap-2 sm:flex-row sm:items-center sm:justify-start sm:gap-3';
  }
}

export function bannerMessageTextClass(align: AppOfferBannerContentAlign): string {
  switch (align) {
    case 'center':
      return 'text-center';
    case 'right':
      return 'text-center sm:text-right';
    default:
      return 'text-center sm:text-left';
  }
}

export function bannerActionsClusterClass(align: AppOfferBannerContentAlign): string {
  const base = 'flex shrink-0 flex-row items-center gap-2';
  if (align === 'center') return `${base} justify-center`;
  return `${base} justify-center sm:justify-end`;
}

export type CtaButtonPresentationStyle = {
  color?: string;
  borderColor?: string;
  backgroundColor?: string;
};

/** Class + inline styles for button-style CTA (link style ignores this). */
export function getCtaButtonPresentation(
  variant: AppOfferBannerCtaButtonVariant,
  radius: AppOfferBannerCtaButtonRadius,
  opts: { ctaTextColor: string; ctaButtonBorderColor: string; ctaButtonBgColor: string }
): { className: string; style: CtaButtonPresentationStyle } {
  const { ctaTextColor, ctaButtonBorderColor, ctaButtonBgColor } = opts;
  const rad = ctaButtonRadiusClass(radius);
  const base = `inline-flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${rad}`;
  if (variant === 'solid') {
    return {
      className: `${base} border border-transparent`,
      style: {
        color: ctaTextColor,
        backgroundColor: (ctaButtonBgColor || '').trim() || 'rgba(255,255,255,0.2)',
      },
    };
  }
  if (variant === 'ghost') {
    return {
      className: `${base} border-0 bg-transparent hover:bg-white/10`,
      style: { color: ctaTextColor },
    };
  }
  return {
    className: `${base} border bg-transparent hover:bg-white/10`,
    style: { color: ctaTextColor, borderColor: ctaButtonBorderColor },
  };
}

export interface AppOfferBanner {
  id: string;
  isActive: boolean;
  priority: number;
  startsAt: Timestamp | null;
  endsAt: Timestamp | null;
  pathRules: string[];
  badgeText: string;
  badgeBgColor: string;
  badgeTextColor: string;
  /** Main message, promo chip, and dismiss control; defaults to white when unset (older docs). */
  messageTextColor?: string;
  /** CTA label (button or link); defaults to white when unset. */
  ctaTextColor?: string;
  /** Border for button-style CTA; defaults to light when unset. Ignored for link style. */
  ctaButtonBorderColor?: string;
  /** Used as the banner fill when `backgroundImageUrl` is empty. */
  backgroundCss: string;
  /** Optional background photo (https URL or same-site path). When set, fills the bar with cover; CSS background is not applied. */
  backgroundImageUrl: string;
  message: string;
  promoCode: string;
  ctaLabel: string;
  ctaUrl: string;
  /** Defaults to `url` when missing (older documents). */
  ctaTarget: AppOfferBannerCtaTarget;
  /** Defaults to `button` when missing (older documents). */
  ctaStyle: AppOfferBannerCtaStyle;
  /** Horizontal layout; default left. */
  contentAlign?: AppOfferBannerContentAlign;
  /** When false, badge is hidden even if `badgeText` is set. Default true. */
  showBadge?: boolean;
  /** When false, no CTA control is shown (announcement-only). Default true. */
  showCta?: boolean;
  /** Used when `ctaStyle` is `button`. Default outline. */
  ctaButtonVariant?: AppOfferBannerCtaButtonVariant;
  /** Used when `ctaStyle` is `button` and variant is solid. */
  ctaButtonBgColor?: string;
  /** Corner radius for button-style CTA. */
  ctaButtonRadius?: AppOfferBannerCtaButtonRadius;
  dismissible: boolean;
  /** After the user dismisses, hide this banner in the browser for this many full days, then show again. Ignored when not dismissible. */
  dismissHideDays?: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/** Default when older Firestore docs omit `dismissHideDays`. */
export const DEFAULT_DISMISS_HIDE_DAYS = 14;

export function clampDismissHideDays(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_DISMISS_HIDE_DAYS;
  return Math.min(3650, Math.max(1, Math.round(n)));
}

export type AppOfferBannerInput = Omit<AppOfferBanner, 'id' | 'createdAt' | 'updatedAt'>;

/** Firestore rejects `undefined` field values — strip before write. */
function omitUndefined<T extends Record<string, unknown>>(obj: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/** URL safe for CSS `background-image: url(...)`, or null if empty/invalid. */
export function safeBannerBackgroundImageUrl(raw: string | undefined | null): string | null {
  if (raw == null) return null;
  const t = String(raw).trim();
  if (!t) return null;
  if (t.startsWith('//')) return null;
  const lower = t.toLowerCase();
  if (lower.startsWith('javascript:') || lower.startsWith('data:') || lower.startsWith('vbscript:')) return null;
  if (t.startsWith('/')) {
    if (/[\s"'<>]/.test(t)) return null;
    return t;
  }
  try {
    const u = new URL(t);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.toString();
  } catch {
    return null;
  }
}

function now(): Timestamp {
  return Timestamp.now();
}

function withinSchedule(b: AppOfferBanner, at: Timestamp): boolean {
  if (b.startsAt && at < b.startsAt) return false;
  if (b.endsAt && at > b.endsAt) return false;
  return true;
}

function matchesPath(b: AppOfferBanner, pathname: string): boolean {
  const rules = b.pathRules || [];
  if (!rules.length) return true;
  return rules.some((prefix) => pathname.startsWith(prefix));
}

export async function listAppOfferBannersAdmin(): Promise<AppOfferBanner[]> {
  const q = query(collection(db, BANNERS_COLLECTION), orderBy('priority', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<AppOfferBanner, 'id'>) }));
}

/** Active + schedule + path; highest priority wins. */
export async function getEligibleBannerForPath(pathname: string): Promise<AppOfferBanner | null> {
  const q = query(collection(db, BANNERS_COLLECTION), where('isActive', '==', true));
  const snap = await getDocs(q);
  const at = now();
  const candidates: AppOfferBanner[] = [];
  for (const d of snap.docs) {
    const b = { id: d.id, ...(d.data() as Omit<AppOfferBanner, 'id'>) };
    if (!withinSchedule(b, at)) continue;
    if (!matchesPath(b, pathname)) continue;
    candidates.push(b);
  }
  candidates.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  return candidates[0] ?? null;
}

export async function createAppOfferBanner(data: AppOfferBannerInput): Promise<string> {
  const t = now();
  const docData = omitUndefined({
    ...data,
    ctaTarget: data.ctaTarget === 'upgrade_modal' ? 'upgrade_modal' : 'url',
    ctaStyle: data.ctaStyle === 'link' ? 'link' : 'button',
    pathRules: data.pathRules || [],
    backgroundImageUrl: data.backgroundImageUrl?.trim() || '',
    createdAt: t,
    updatedAt: t,
  });
  const ref = await addDoc(collection(db, BANNERS_COLLECTION), docData as DocumentData);
  return ref.id;
}

export async function updateAppOfferBanner(
  id: string,
  data: Partial<AppOfferBannerInput>
): Promise<void> {
  const ref = doc(db, BANNERS_COLLECTION, id);
  const patch = omitUndefined({ ...data, updatedAt: now() } as Record<string, unknown>);
  await updateDoc(ref, patch as DocumentData);
}

export async function deleteAppOfferBanner(id: string): Promise<void> {
  await deleteDoc(doc(db, BANNERS_COLLECTION, id));
}

export async function logBannerEvent(
  bannerId: string,
  type: AppOfferBannerEventType,
  pathname: string,
  userId: string,
  options?: { ctaClickKind?: AppOfferBannerCtaClickKind }
): Promise<void> {
  const ctaClickKind: 'none' | AppOfferBannerCtaClickKind =
    type === 'cta_click' ? options?.ctaClickKind ?? 'button' : 'none';
  await addDoc(collection(db, ANALYTICS_COLLECTION), {
    bannerId,
    type,
    path: pathname,
    userId,
    createdAt: now(),
    ctaClickKind,
  });
}

export type BannerAnalyticsCounts = {
  impressions: number;
  dismissClicks: number;
  ctaClicks: number;
  ctaClicksLink: number;
  ctaClicksButton: number;
  ctaClicksUpgradeModal: number;
  /** `cta_click` rows missing `ctaClickKind` (older clients). */
  ctaClicksLegacy: number;
};

export async function getBannerEventCounts(bannerId: string): Promise<BannerAnalyticsCounts> {
  const base = collection(db, ANALYTICS_COLLECTION);
  const [impSnap, disSnap, ctaTotalSnap, ctaLinkSnap, ctaBtnSnap, ctaUpSnap] = await Promise.all([
    getCountFromServer(query(base, where('bannerId', '==', bannerId), where('type', '==', 'impression'))),
    getCountFromServer(query(base, where('bannerId', '==', bannerId), where('type', '==', 'dismiss'))),
    getCountFromServer(query(base, where('bannerId', '==', bannerId), where('type', '==', 'cta_click'))),
    getCountFromServer(
      query(base, where('bannerId', '==', bannerId), where('type', '==', 'cta_click'), where('ctaClickKind', '==', 'link'))
    ),
    getCountFromServer(
      query(base, where('bannerId', '==', bannerId), where('type', '==', 'cta_click'), where('ctaClickKind', '==', 'button'))
    ),
    getCountFromServer(
      query(
        base,
        where('bannerId', '==', bannerId),
        where('type', '==', 'cta_click'),
        where('ctaClickKind', '==', 'upgrade_modal')
      )
    ),
  ]);
  const ctaClicks = ctaTotalSnap.data().count;
  const ctaClicksLink = ctaLinkSnap.data().count;
  const ctaClicksButton = ctaBtnSnap.data().count;
  const ctaClicksUpgradeModal = ctaUpSnap.data().count;
  const ctaClicksLegacy = Math.max(0, ctaClicks - ctaClicksLink - ctaClicksButton - ctaClicksUpgradeModal);
  return {
    impressions: impSnap.data().count,
    dismissClicks: disSnap.data().count,
    ctaClicks,
    ctaClicksLink,
    ctaClicksButton,
    ctaClicksUpgradeModal,
    ctaClicksLegacy,
  };
}
